import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MenuItem, usePackContext } from "../../context/pack"; // 경로 수정
import { gql, useMutation, useQuery } from "@apollo/client";
import Modal from "../../modal"; // 모달 컴포넌트 추가
import PreviewModal from "./preview-modal"; // 미리보기 모달 컴포넌트 추가
import Header from "../../../../components/common/header";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { track } from "@amplitude/analytics-browser";
import { getCDNResizedImageUrl } from "../../../../utils/get-cdn-resized-image-url";
import { notify, notifyError } from "../../../../utils/toast";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";

const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
      price
      discountPrice
      description
      saleEndTime
    }
  }
`;

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  contactNumber: string;
  closingHours: string;
  liveOrderNotice?: string;
  lat: number;
  lng: number;
};

const LiveOrderUpload: React.FC = () => {
  const navigate = useNavigate();
  const {
    packs,
    resetPacks,
    deletePack,
    updatePackQuantity,
    updateItemQuantity,
  } = usePackContext(); // Context에서 packs, resetPacks 및 deletePack 불러오기

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [closingTime, setClosingTime] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);

  const [store, setStore] = useState<Store | null>(null);
  const { loading } = useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
      setClosingTime(data.getSellerStore.closingHours);
      setNotice(data.getSellerStore.liveOrderNotice || "");
    },
  });

  // useMutation 훅을 사용해 createProduct API와 연결
  const [createProduct] = useMutation(CREATE_PRODUCT);

  // 스크롤 초기화
  useEffect(() => {
    if (!loading) {
      // 다음 이벤트 루프에서 스크롤 맨 위로 초기화
      setTimeout(() => {
        containerRef.current?.scrollTo(0, 0);
      }, 0);
    }
  }, [loading]);

  const calculateTotalPrice = (pack: MenuItem[]): number => {
    return pack.reduce(
      (total, item) => total + item.price * (item.quantity || 1),
      0
    );
  };

  const calculateDiscountedPrice = (pack: MenuItem[]): number => {
    const discountRate = pack[0]?.discountRate ?? 0; // 동일한 할인율 적용
    const totalPrice = calculateTotalPrice(pack);
    return totalPrice - totalPrice * (discountRate / 100);
  };

  const handleComplete = async () => {
    const currentTime = new Date();
    const closingHour = parseInt(closingTime.split(":")[0]);
    const closingMinute = parseInt(closingTime.split(":")[1]);
    const closingDate = new Date(
      currentTime.getFullYear(),
      currentTime.getMonth(),
      currentTime.getDate(),
      closingHour,
      closingMinute
    );

    if (currentTime > closingDate) {
      alert("시간이 지났습니다. 다시 시도해주세요.");
      return; // 더 이상 진행하지 않음
    }

    setIsModalOpen(true); // 완료하기 버튼 클릭 시 모달 열기
  };
  const onConfirm = async () => {
    try {
      // 각 팩을 createProduct로 전송
      for (const pack of packs) {
        let menus;
        let img;
        if (pack.items[0].id !== 0) {
          // 메뉴선택이란뜻임,
          menus = pack.items.map((item) => ({
            menuId: item.id,
            quantity: item.quantity || 1,
            img: item.img,
          }));
        } else {
          // 직접선택
          img = pack.items[0].img;
        }
        const packName = pack.items.map((item) => item.name).join(" + ");

        // 서버에 createProduct 요청
        await createProduct({
          variables: {
            input: {
              name: packName,
              price: calculateTotalPrice(pack.items),
              discountPrice: calculateDiscountedPrice(pack.items),
              discountRate: pack.items[0].discountRate,
              description: ``,
              saleEndTime: new Date().toISOString(), // 판매 종료 시간 설정 (필요에 따라 수정 가능)
              menus, // 메뉴에서 선택했을 때에만 이걸 전송, 직접 입력 시 빈배열 전달.
              quantity: pack.quantity,
              img, // 이미지가 없어도 서버에서 알아서 처리함.
              isSampleMenu: pack.isSampleMenu,
              closingHours: closingTime,
              liveOrderNotice: notice,
            },
          },
        });
        track("제품 생성", {
          방식: pack.items[0].id !== 0 ? "메뉴 선택" : "직접 입력",
          할인가격: calculateDiscountedPrice(pack.items),
          할인율: Math.floor(
            ((calculateTotalPrice(pack.items) -
              calculateDiscountedPrice(pack.items)) /
              calculateTotalPrice(pack.items)) *
              100
          ),
          매장명: store?.title,
        });
      }
      resetPacks(); // 팩 초기화
      navigate("/boss/home"); // 성공 후 홈으로 이동
      notify("판매가 시작됐어요!");
    } catch (error) {
      console.error("Error creating product:", error);
      notifyError("오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  const adjustTime = (minutes: number) => {
    const [hours, mins] = closingTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, mins + minutes);

    const newHours = date.getHours().toString().padStart(2, "0");
    const newMinutes = date.getMinutes().toString().padStart(2, "0");
    setClosingTime(`${newHours}:${newMinutes}`);
  };

  if (loading) return <LoadingSpinnerFull />;

  if (!store)
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );

  return (
    <div
      ref={containerRef}
      className="h-screen bg-white pt-0 pb-40 overflow-y-scroll"
    >
      <Header title="최종 확인" showBackButton />

      {/* pb-24로 하단 공간 확보 */}
      <div className="px-4 pt-6">
        {packs.length > 0 ? (
          <div>
            <h3 className="text-2xl font-bold mb-2">선택된 팩들</h3>
            {packs.map((pack, index) => (
              <div
                key={index}
                className="mb-6 p-4 rounded-lg border border-gray-300 shadow-md bg-[#f9f9f9] relative"
              >
                <button
                  className="absolute top-2 right-2 text-red-500 text-xl"
                  onClick={() => deletePack(index)} // 해당 팩을 삭제하는 함수 호출
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <ul className="space-y-2">
                  {pack.items.map((item, itemIndex) => (
                    <li key={item.id} className="flex items-center space-x-4">
                      <img
                        src={
                          item.img instanceof File
                            ? URL.createObjectURL(item.img)
                            : getCDNResizedImageUrl(item.img, {
                                width: 600,
                                quality: 80,
                              })
                        }
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                      <div className="flex-1">
                        <div className="text-black text-xl">{item.name}</div>
                        <div className="text-lg text-gray-500">
                          {item.price.toLocaleString()}원
                        </div>
                        {/* 수량 입력 필드 */}
                        {pack.items[0].id !== 0 && (
                          <div className="mt-2">
                            <label className="text-lg text-gray-700">
                              수량: {item.quantity || 1}개
                            </label>
                            {/* 아이템별 수량 조절 UI */}
                            <div className="flex items-center mt-2 space-x-2">
                              <button
                                className="w-8 h-8 bg-blue-100 text-blue-600 text-lg rounded-md hover:bg-blue-200 transition duration-150 flex items-center justify-center"
                                onClick={() => {
                                  const currentQuantity = item.quantity || 1;
                                  const newQuantity = Math.max(
                                    1,
                                    currentQuantity - 1
                                  );
                                  updateItemQuantity(
                                    index,
                                    item.id,
                                    newQuantity
                                  );
                                }}
                              >
                                -
                              </button>
                              <span className="w-8 h-8 bg-white border border-gray-300 text-center flex items-center justify-center text-sm font-medium">
                                {item.quantity || 1}
                              </span>
                              <button
                                className="w-8 h-8 bg-blue-100 text-blue-600 text-lg rounded-md hover:bg-blue-200 transition duration-150 flex items-center justify-center"
                                onClick={() => {
                                  const currentQuantity = item.quantity || 1;
                                  const newQuantity = currentQuantity + 1;
                                  updateItemQuantity(
                                    index,
                                    item.id,
                                    newQuantity
                                  );
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {/* 팩 총 가격 및 할인된 가격 표시 */}
                <div className="mt-4">
                  <div className="text-lg">
                    총 가격: {calculateTotalPrice(pack.items).toLocaleString()}
                    원
                  </div>
                  <div className="text-xl text-[#1562fc] font-bold">
                    할인된 가격:{" "}
                    {calculateDiscountedPrice(pack.items).toLocaleString()}원
                  </div>
                </div>
                {/* 팩 전체 수량 조절 - 라벨 추가 및 스타일 개선 */}
                <div className="mt-4">
                  <label className="block text-lg font-semibold text-end text-gray-700 mb-2">
                    이 팩을 몇 팩 판매할까요?
                  </label>
                  <div className="flex justify-end items-center mt-2">
                    <button
                      className="w-12 h-12 bg-gray-300 text-black text-xl rounded-md transition duration-150 mb-4 flex items-center justify-center"
                      onClick={() => {
                        const newQuantity = Math.max(
                          1,
                          (pack.quantity || 1) - 1
                        ); // 최소 수량 1
                        updatePackQuantity(index, newQuantity); // 수량 업데이트
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min={1}
                      value={pack.quantity || 1}
                      onChange={(e) => {
                        const newQuantity = Math.max(1, Number(e.target.value)); // 최소 수량 1
                        updatePackQuantity(index, newQuantity); // 수량 업데이트
                      }}
                      className="w-16 h-12 py-2 bg-white text-xl text-black rounded-md transition duration-150 mb-4 text-center mx-2"
                    />
                    <button
                      className="w-12 h-12 bg-gray-300 text-black text-xl rounded-md transition duration-150 mb-4 flex items-center justify-center"
                      onClick={() => {
                        const newQuantity = (pack.quantity || 1) + 1; // 수량 증가
                        updatePackQuantity(index, newQuantity); // 수량 업데이트
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-800">
            아래 버튼을 클릭하여 오늘의 제품을 올려보세요~!
          </p>
        )}
        {/* 새로운 팩 만들기 버튼 */}
        <div>
          <div
            className="w-full flex flex-col justify-center items-center h-full bg-white border-2 border-dashed border-gray-300 rounded-lg py-4"
            onClick={() => navigate("/boss/live-order/select-from-menu")}
          >
            <div className="text-4xl pb-1 font-bold text-white rounded-full bg-green-500 h-10 w-10 flex items-center justify-center">
              +
            </div>
            <div className="text-3xl text-gray-500">팩 추가</div>
          </div>
        </div>

        {/* 픽업 시간 */}
        <div className="mt-6">
          <label className="block text-2xl font-bold mb-2">
            픽업 시간(~까지)
          </label>
          <div className="text-gray-500 mb-4">
            이 시간까지 픽업이 가능하다고 고객들에게 안내됩니다.
            <br /> 보통 퇴근시간 5-10분 전을 선택합니다!
          </div>
          <div className="flex flex-col items-center justify-center">
            <input
              type="time"
              value={closingTime}
              onChange={(e) => {
                const selectedTime = e.target.value;
                const currentTime = new Date();
                const selectedDateTime = new Date();
                const [hours, minutes] = selectedTime.split(":").map(Number);
                selectedDateTime.setHours(hours, minutes, 0, 0);

                if (selectedDateTime < currentTime) {
                  alert("현재 시간보다 이른 시간은 선택할 수 없어요.");
                } else if (
                  selectedDateTime.getDate() !== currentTime.getDate()
                ) {
                  alert("오늘 날짜를 넘길 수 없습니다.");
                } else {
                  setClosingTime(selectedTime);
                }
              }}
              className="mx-4 mt-6 p-4 rounded-lg font-bold text-lg"
              autoFocus
              style={{ fontSize: "1.5rem", height: "3rem" }}
            />
            <div className="flex items-center my-6 space-x-2">
              <button
                onClick={() => adjustTime(-10)}
                className="p-2 bg-red-100 text-red-500 rounded-lg"
              >
                -10분
              </button>
              <button
                onClick={() => adjustTime(-5)}
                className="p-2 bg-red-100 text-red-500 rounded-lg"
              >
                -5분
              </button>
              <button
                onClick={() => adjustTime(5)}
                className="p-2 bg-[#1692fc] text-white rounded-lg"
              >
                +5분
              </button>
              <button
                onClick={() => adjustTime(10)}
                className="p-2 bg-[#1692fc] text-white rounded-lg"
              >
                +10분
              </button>
            </div>
          </div>
        </div>

        {/* 실시간 판매 공지 */}
        <div className="mt-6">
          <label className="block text-2xl font-bold mb-2">
            구매 전 알림(특이사항){" "}
            <span className="text-gray-500 text-lg font-normal">(선택)</span>
          </label>
          <div className="text-gray-500 mb-2">
            고객이 상품을 구매하기 전에 볼 수 있는 공지사항을 작성해주세요.
          </div>

          <textarea
            value={notice}
            onChange={(e) => setNotice(e.target.value)}
            placeholder="공지 내용을 입력해주세요 (예: 매장 참고사항, 픽업 방법 안내, 알러지 정보 등)"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
            rows={3}
          />

          {/* 최근 사용한 공지 가져왔다는 안내 */}
          {notice && notice === store?.liveOrderNotice && (
            <p className="mt-2 text-sm text-gray-500">
              최근 사용한 공지를 가져왔습니다.
            </p>
          )}
        </div>

        {/* 완료하기 버튼 - 화면 하단에 고정 */}
        <div className="w-full left-0 bg-white mt-8">
          <button
            onClick={handleComplete}
            className="w-full py-3 bg-[#1562fc] text-xl text-white rounded-md active:bg-[#124ab7] transition duration-150"
          >
            완료하기
          </button>
        </div>
        {/* 미리보기 버튼 */}
        {packs.length > 0 && (
          <div className="w-full left-0 bg-white mt-2">
            <button
              onClick={() => {
                setIsPreviewModalOpen(true);
                track("미리보기 버튼 클릭", {
                  어디서: "실시간업로드",
                });
              }}
              className="w-full py-3 bg-gray-500 text-xl text-white rounded-md active:bg-gray-600 transition duration-150 mb-3"
            >
              미리보기
            </button>
          </div>
        )}
        {/* 모달 */}
        {isModalOpen && (
          <Modal
            title="게시 확인"
            message="정말로 게시하시겠어요? 소비자에게 노출됩니다."
            onConfirm={onConfirm}
            onCancel={() => setIsModalOpen(false)} // 모달 취소
          />
        )}

        {/* 미리보기 모달 */}
        <PreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          packs={packs}
          closingTime={closingTime}
          storeName={store?.title || ""}
          notice={notice}
        />
      </div>
    </div>
  );
};

export default LiveOrderUpload;
